function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr && (typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]);

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

import React, { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { nanoid } from "nanoid";
import useField from "../hooks/internal/useField";
import { pluralize } from "../utils/string";
import { mergeProps } from "../utils/component";
import Field from "./internal/Field";
import Grid from "./Grid";
import InternalInput from "./internal/InternalInput";
import { jsx as ___EmotionJSX } from "@emotion/core";
var COLORS = InternalInput.COLORS;
var YEARS_REGEX = /^\d{1,2}$/;
var MONTHS_REGEX = /^\d{1,2}$/;
var DEFAULT_PROPS = {
  color: InternalInput.DEFAULT_PROPS.color,
  disabled: false,
  optional: false,
  validate: function validate(_ref) {
    var years = _ref.years,
        months = _ref.months;
    var errors = [];

    if (YEARS_REGEX.test(years)) {
      var yearsInt = parseInt(years, 10);

      if (yearsInt < 0 || yearsInt > 99) {
        errors.push("Years must be within 0-99.");
      }
    } else if (years !== "") {
      errors.push("Years must be within 0-99.");
    }

    if (MONTHS_REGEX.test(months)) {
      var monthsInt = parseInt(months, 10);

      if (monthsInt < 0 || monthsInt > 11) {
        errors.push("Months must be within 0-11.");
      }
    } else if (months !== "") {
      errors.push("Months must be within 0-11.");
    }

    if (errors.length === 0) {
      var _yearsInt = parseInt(years || "0", 10);

      var _monthsInt = parseInt(months || "0", 10);

      if (_yearsInt === 0 && _monthsInt === 0) {
        errors.push("Must be at least 1 month.");
      }
    }

    return errors;
  }
};

function getHelpText(years, months, defaultHelpText) {
  var yearsInt = parseInt(years || "0", 10);
  var monthsInt = parseInt(months || "0", 10);

  if (isNaN(yearsInt) || isNaN(monthsInt) || yearsInt === 0 && monthsInt === 0) {
    return defaultHelpText;
  }

  return [{
    count: yearsInt,
    word: "year"
  }, {
    count: monthsInt,
    word: "month"
  }].filter(function (_ref2) {
    var count = _ref2.count;
    return count > 0;
  }).map(function (_ref3) {
    var count = _ref3.count,
        word = _ref3.word;
    return pluralize(count, word);
  }).join(" and ");
}

TimeSpan.COLORS = COLORS;
TimeSpan.DEFAULT_PROPS = DEFAULT_PROPS;
TimeSpan.getHelpText = getHelpText;

function TimeSpan(props) {
  var mergedProps = mergeProps(props, DEFAULT_PROPS, {}, {
    color: function color(_color) {
      return COLORS.includes(_color);
    },
    disabled: function disabled(_disabled) {
      return typeof _disabled === "boolean";
    },
    optional: function optional(_optional) {
      return typeof _optional === "boolean";
    }
  });
  var name = mergedProps.name,
      label = mergedProps.label,
      helpTextProp = mergedProps.helpText,
      disabled = mergedProps.disabled,
      optional = mergedProps.optional,
      validate = mergedProps.validate,
      validateData = mergedProps.validateData,
      testId = mergedProps.testId,
      __internal__yearsFocus = mergedProps.__internal__yearsFocus,
      __internal__monthsFocus = mergedProps.__internal__monthsFocus;

  var _useState = useState(function () {
    return "time-span-".concat(nanoid());
  }),
      _useState2 = _slicedToArray(_useState, 1),
      labelId = _useState2[0];

  var _useState3 = useState(function () {
    return "time-span-aux-".concat(nanoid());
  }),
      _useState4 = _slicedToArray(_useState3, 1),
      auxId = _useState4[0];

  var isEmpty = useCallback(function (value) {
    return value.years.trim() === "" && value.months.trim() === "";
  }, []);
  var data = useMemo(function () {
    return _objectSpread({
      isEmpty: isEmpty
    }, validateData && {
      data: validateData
    });
  }, [isEmpty, validateData]);

  var _useField = useField("TimeSpan", {
    name: name,
    disabled: disabled,
    optional: optional,
    validate: validate,
    data: data
  }),
      value = _useField.value,
      errors = _useField.errors,
      hasErrors = _useField.hasErrors,
      onFocus = _useField.onFocus,
      onBlur = _useField.onBlur,
      onChange = _useField.onChange;

  var helpText = useMemo(function () {
    return getHelpText(value.years, value.months, helpTextProp);
  }, [value.years, value.months, helpTextProp]);
  return ___EmotionJSX(Field, {
    optional: optional,
    disabled: disabled,
    label: label,
    labelId: labelId,
    auxId: auxId,
    helpText: helpText,
    errors: errors,
    testId: testId
  }, ___EmotionJSX("div", {
    "aria-invalid": hasErrors ? "true" : null,
    "aria-labelledby": labelId,
    "aria-describedby": helpText || hasErrors ? auxId : null
  }, ___EmotionJSX(Grid, {
    cols: 2,
    colsGap: 1
  }, ___EmotionJSX(Grid.Item, {
    colSpan: "0"
  }, ___EmotionJSX(InternalInput, {
    name: "".concat(name, ".years"),
    parentName: name,
    variant: "numeric",
    color: props.color,
    placeholder: "Years",
    disabled: disabled,
    onFocus: onFocus,
    onBlur: onBlur,
    value: value.years,
    onChange: onChange,
    __internal__focus: __internal__yearsFocus
  })), ___EmotionJSX(Grid.Item, {
    colSpan: "1"
  }, ___EmotionJSX(InternalInput, {
    name: "".concat(name, ".months"),
    parentName: name,
    variant: "numeric",
    color: props.color,
    placeholder: "Months",
    disabled: disabled,
    onFocus: onFocus,
    onBlur: onBlur,
    value: value.months,
    onChange: onChange,
    __internal__focus: __internal__monthsFocus
  })))));
}

TimeSpan.propTypes = {
  name: PropTypes.string.isRequired,
  color: PropTypes.oneOf(COLORS),
  label: PropTypes.string.isRequired,
  helpText: PropTypes.string,
  disabled: PropTypes.bool,
  optional: PropTypes.bool,
  validate: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  validateData: PropTypes.any,
  testId: PropTypes.string,
  __internal__yearsFocus: PropTypes.bool,
  __internal__monthsFocus: PropTypes.bool
};
export default TimeSpan;